/**
 * Guest Authentication Service for LEXE Webchat
 *
 * Creates ephemeral sessions for anonymous webchat users without requiring login.
 * Guest sessions have limited permissions (chat:read, chat:write) and expire in 24 hours.
 *
 * Flow:
 * 1. Check for stored guest tokens (localStorage)
 * 2. If valid, use existing session
 * 3. If expired/missing, create new guest session via /auth/guest
 * 4. Store tokens for session persistence across page reloads
 */

const GUEST_TOKEN_KEY = "lexe-guest-token";
const DEVICE_ID_KEY = "lexe-device-id";

/**
 * Guest token response from backend
 */
export interface GuestTokens {
  accessToken: string;
  tokenType: string;
  expiresAt: number;
  guestId: string;
  contactId: string;
}

/**
 * Get or create a persistent device ID for session continuity
 */
function getDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Get stored guest tokens from localStorage
 * Returns null if tokens are expired or not found
 */
export function getStoredGuestTokens(): GuestTokens | null {
  try {
    const stored = localStorage.getItem(GUEST_TOKEN_KEY);
    if (!stored) return null;

    const tokens: GuestTokens = JSON.parse(stored);

    // Check if token is expired (with 5 minute buffer)
    const bufferMs = 5 * 60 * 1000;
    if (tokens.expiresAt - bufferMs < Date.now()) {
      localStorage.removeItem(GUEST_TOKEN_KEY);
      return null;
    }

    return tokens;
  } catch {
    // Invalid JSON or other error
    localStorage.removeItem(GUEST_TOKEN_KEY);
    return null;
  }
}

/**
 * Store guest tokens in localStorage
 */
function storeGuestTokens(tokens: GuestTokens): void {
  localStorage.setItem(GUEST_TOKEN_KEY, JSON.stringify(tokens));
}

/**
 * Clear guest tokens from localStorage
 */
export function clearGuestTokens(): void {
  localStorage.removeItem(GUEST_TOKEN_KEY);
}

/**
 * Create a new guest session via the backend API
 *
 * @throws Error if VITE_TENANT_ID is not configured
 * @throws Error if API call fails
 */
export async function createGuestSession(): Promise<GuestTokens> {
  const tenantId = import.meta.env.VITE_TENANT_ID;

  if (!tenantId) {
    throw new Error(
      "[GuestAuth] VITE_TENANT_ID not configured - required for guest auth"
    );
  }

  // Get API URL - guest endpoint is at /api/auth/guest (not /api/v1/auth/guest)
  const apiUrl =
    import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";
  const guestUrl = `${apiUrl.replace(/\/api\/v1$/, "")}/api/auth/guest`;

  console.log("[GuestAuth] Creating guest session...", {
    url: guestUrl,
    tenantId,
  });

  const response = await fetch(guestUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tenant-ID": tenantId,
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      device_id: getDeviceId(),
      metadata: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        timestamp: new Date().toISOString(),
      },
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("[GuestAuth] Failed to create guest session:", {
      status: response.status,
      error: errorText,
    });
    throw new Error(`Guest auth failed: ${response.status} ${errorText}`);
  }

  const data = await response.json();

  // Transform snake_case response to camelCase
  const tokens: GuestTokens = {
    accessToken: data.access_token,
    tokenType: data.token_type,
    expiresAt: Date.now() + data.expires_in * 1000,
    guestId: data.guest_id,
    contactId: data.contact_id,
  };

  // Store for persistence
  storeGuestTokens(tokens);

  console.log("[GuestAuth] Guest session created:", {
    guestId: tokens.guestId,
    contactId: tokens.contactId,
    expiresIn: `${Math.round(data.expires_in / 3600)}h`,
  });

  return tokens;
}

/**
 * Get or create guest session
 *
 * Checks for existing valid tokens first, creates new session if needed.
 */
export async function getOrCreateGuestSession(): Promise<GuestTokens> {
  // Try stored tokens first
  const stored = getStoredGuestTokens();
  if (stored) {
    console.log("[GuestAuth] Using stored guest session:", {
      guestId: stored.guestId,
      contactId: stored.contactId,
      expiresIn: `${Math.round((stored.expiresAt - Date.now()) / 3600000)}h`,
    });
    return stored;
  }

  // Create new session
  return createGuestSession();
}

/**
 * Check if we have a valid guest session
 */
export function hasValidGuestSession(): boolean {
  return getStoredGuestTokens() !== null;
}

/**
 * Get the current guest access token (if valid)
 */
export function getGuestAccessToken(): string | null {
  const tokens = getStoredGuestTokens();
  return tokens?.accessToken ?? null;
}
